import { Component, computed, inject, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatToolbar } from '@angular/material/toolbar';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { AuthStorageService } from '@core/services/auth-storage.service';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterLink } from '@angular/router';
import { LogoutUseCaseService } from '@core/use-cases/auth/logout.use-case.service';
import {
  VdoButtonComponent,
  VdoButtonIconComponent,
  VdoIconComponent,
  VdoToastrService,
} from 'ui-kit';
import { LanguageStorageService } from '@core/services/language-storage.service';
import { ProfileDialogComponent } from './dialogs/profile-dialog/profile-dialog.component';
import { UserSessionStorageService } from '@core/services/user-session-storage.service';
import { finalize } from 'rxjs/operators';
import { AsyncPipe, CommonModule } from '@angular/common';

@Component({
  selector: 'vdo-header',
  imports: [
    CommonModule,
    VdoIconComponent,
    VdoButtonIconComponent,
    MatToolbar,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    VdoButtonComponent,
    TranslatePipe,
    MatProgressSpinnerModule,
    AsyncPipe,
    RouterLink,
  ],
  templateUrl: './vdo-header.component.html',
  styleUrl: './vdo-header.component.scss',
})
export class VdoHeaderComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly logoutUseCase = inject(LogoutUseCaseService);
  private readonly router = inject(Router);
  private readonly languageStorageService = inject(LanguageStorageService);
  readonly authStorageService = inject(AuthStorageService);
  private readonly dialog = inject(MatDialog);
  private readonly vdoToastrService = inject(VdoToastrService);

  private readonly userSessionStorageService = inject(UserSessionStorageService);

  protected readonly user = computed(() => this.userSessionStorageService.userSession());
  protected isLoggingOut: boolean = false;

  ngOnInit(): void {
    const currentLang = this.translateService.currentLang;
    const storedLang = this.languageStorageService.get();

    if (currentLang && currentLang !== storedLang) {
      this.languageStorageService.setSafe(currentLang);
    }
  }

  langGetCurrentIcon(): string {
    const langCurrent = this.translateService.currentLang;
    switch (langCurrent) {
      case 'pt-br':
        return 'vdo-lang-pt-br';
      case 'en':
        return 'vdo-lang-gb';
      case 'es':
        return 'vdo-lang-es';
      default:
        return 'vdo-lang-pt';
    }
  }

  langChange(lang: string): void {
    this.translateService.use(lang);
    this.languageStorageService.setSafe(lang);
  }

  onProfileClicked(): void {
    this.dialog.open(ProfileDialogComponent, {
      width: '611px',
      maxWidth: '90vw',
      panelClass: 'profile-dialog-panel',
      disableClose: true,
      autoFocus: false,
    });
  }

  onLogoutClicked(): void {
    this.isLoggingOut = true;
    this.logoutUseCase
      .execute()
      .pipe(
        finalize(() => {
          this.isLoggingOut = false;
        })
      )
      .subscribe({
        next: response => {
          this.router.navigate([response.logoutUrl], { queryParams: { logoutSuccess: 'true' } });
        },
        error: error => {
          console.error('Logout error:', error);
          this.vdoToastrService.showError(
            this.translateService.instant('LOGIN.MESSAGES.LOGOUT_ERROR')
          );
          this.router.navigate(['/login']);
        },
      });
  }
}
